import { CompanyOrEmployeeKeyParams } from '@util/employeePreferences';
import { QueryMeta, queryClient } from '.';
import { ROOM_OPTION_LIST_QUERY_KEY } from './queryKeys';

export class MutationError extends Error {
  constructor(
    message: string,
    readonly type: string | undefined
  ) {
    super(message);
  }
}

type ListType = 'room-option';

const getListQueryKey = (listType: ListType) => {
  return ROOM_OPTION_LIST_QUERY_KEY;
};

export const invalidateListByJobKeyQueries = async (
  listType: ListType,
  { companyKey, employeeKey }: CompanyOrEmployeeKeyParams
) => {
  const listQueryKey = getListQueryKey(listType);

  const keyPrefixes = ['mv', companyKey || employeeKey];

  await queryClient.invalidateQueries({
    predicate: (query) => {
      const { queryKey } = query;

      const hasBothJobAndEntityKeys = keyPrefixes.every((prefix) =>
        queryKey.some((key) => typeof key === 'string' && prefix && key.startsWith(prefix))
      );

      return queryKey.includes(listQueryKey) && hasBothJobAndEntityKeys;
    },
  });
};

// Invalidate all queries related to the employee
// This includes both the employee's default queries and any queries scoped by jobKey
export const invalidateAllEmployeeListsQueries = async (employeeKey: string) => {
  const listsBasicQueryKeysToInvalidate = [ROOM_OPTION_LIST_QUERY_KEY];

  await Promise.all(
    listsBasicQueryKeysToInvalidate.map((listKey) =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey.includes(employeeKey) && queryKey.includes(listKey),
      })
    )
  );
};

export const invalidateAllCompanyEmployeesListQueries = async (listType: ListType, companyKey: string) => {
  const listQueryKey = getListQueryKey(listType);

  await Promise.all([
    // Invalidate all lists queries for all employees
    // These queries include the companyKeys in their meta
    queryClient.invalidateQueries({
      predicate: (query) => {
        const { queryKey } = query;
        const companyKeys = (query.meta as QueryMeta)?.companyKeys ?? [];

        const hasEmployeeKeyInQueryKey = queryKey.some((key) => typeof key === 'string' && key.startsWith('emp'));
        const hasCompanyKeyInMeta = companyKeys.includes(companyKey);

        return queryKey.includes(listQueryKey) && hasCompanyKeyInMeta && hasEmployeeKeyInQueryKey;
      },
      refetchType: 'all',
    }),
    // Invalidate all job scoped queries for all employees under the company
    invalidateListByJobKeyQueries(listType, { companyKey }),
  ]);
};
